import React from 'react';
import { MetricLabel } from './MetricLabel';
import { formatCurrency } from '../../utils/formatters';

interface FinancialWaterfallCardProps {
  // Income chain
  gpi: number;
  vacancyRate: number;
  vacancyLoss: number;
  egi: number;
  opexRate: number;
  opexAmount: number;
  noi: number;

  // Debt & cash flow
  annualDebtService: number;
  preTaxCashFlow: number;
  year1TaxBenefit: number;
  afterTaxCashFlow: number;

  // Return metric
  equityInvested: number;
  cashOnCash: number;

  // Optional
  noiLabel?: string;
  scenarioLabel?: string;
  className?: string;
}

function WaterfallRow({
  label,
  value,
  type = 'neutral',
  bold = false,
  large = false,
}: {
  label: React.ReactNode;
  value: string;
  type?: 'neutral' | 'subtract' | 'add' | 'subtotal' | 'result';
  bold?: boolean;
  large?: boolean;
}) {
  const colorMap = {
    neutral: 'text-ivory',
    subtract: 'text-crimson',
    add: 'text-emerald',
    subtotal: 'text-gold',
    result: 'text-gold',
  };
  const prefix = type === 'subtract' ? '\u2212' : type === 'add' ? '+' : type === 'subtotal' ? '=' : '';

  return (
    <div className={`flex justify-between items-center py-1.5 ${
      type === 'subtotal' ? 'border-t border-gold/30 mt-1 pt-2' : ''
    }`}>
      <span className={`text-sm ${type === 'subtotal' || bold ? 'text-ivory font-semibold' : 'text-stone-400'}`}>
        {prefix && <span className={`inline-block w-4 ${colorMap[type]}`}>{prefix}</span>}
        {label}
      </span>
      <span className={`font-mono tabular-nums ${colorMap[type]} ${bold ? 'font-semibold' : ''} ${large ? 'text-xl font-bold' : 'text-sm'}`}>
        {value}
      </span>
    </div>
  );
}

export function FinancialWaterfallCard({
  gpi,
  vacancyRate,
  vacancyLoss,
  egi,
  opexRate,
  opexAmount,
  noi,
  annualDebtService,
  preTaxCashFlow,
  year1TaxBenefit,
  afterTaxCashFlow,
  equityInvested,
  cashOnCash,
  noiLabel,
  scenarioLabel,
  className = '',
}: FinancialWaterfallCardProps) {
  // Guard: if no meaningful income data, don't render
  if (!gpi || gpi <= 0) return null;

  const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
  const fmtDollar = (v: number) => formatCurrency(Math.abs(v));

  return (
    <div className={`bg-charcoal border border-slate-dark rounded-lg p-5 ${className}`}>
      {/* Section 1: Income → NOI */}
      <div className="mb-4">
        <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">Income to NOI</div>
        <WaterfallRow
          label={<MetricLabel metric="gpi">Gross Potential Income</MetricLabel>}
          value={fmtDollar(gpi)}
        />
        <WaterfallRow
          label={<MetricLabel metric="vacancy_loss">Vacancy Loss ({fmtPct(vacancyRate)})</MetricLabel>}
          value={fmtDollar(vacancyLoss)}
          type="subtract"
        />
        <WaterfallRow
          label={<MetricLabel metric="egi">Effective Gross Income</MetricLabel>}
          value={fmtDollar(egi)}
          type="subtotal"
        />
        <WaterfallRow
          label={<MetricLabel metric="opex_ratio">Operating Expenses ({fmtPct(opexRate)} of EGI)</MetricLabel>}
          value={fmtDollar(opexAmount)}
          type="subtract"
        />
        <WaterfallRow
          label={<MetricLabel metric="noi">{noiLabel ? `${noiLabel} NOI` : 'Net Operating Income'}</MetricLabel>}
          value={fmtDollar(noi)}
          type="subtotal"
          bold
        />
      </div>

      {/* Divider */}
      <div className="border-t border-gold/30 my-3" />

      {/* Section 2: NOI → After-Tax Cash Flow */}
      <div className="mb-4">
        <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">
          NOI to Cash Flow
          {scenarioLabel && (
            <span className="ml-2 text-gold/80 normal-case">({scenarioLabel})</span>
          )}
        </div>
        <WaterfallRow
          label={<MetricLabel metric="debt_service">Annual Debt Service</MetricLabel>}
          value={fmtDollar(annualDebtService)}
          type="subtract"
        />
        <WaterfallRow
          label={<MetricLabel metric="pre_tax_cf">Pre-Tax Cash Flow</MetricLabel>}
          value={fmtDollar(preTaxCashFlow)}
          type="subtotal"
        />
        <WaterfallRow
          label={<MetricLabel metric="obbb">Year 1 Tax Benefits</MetricLabel>}
          value={fmtDollar(year1TaxBenefit)}
          type="add"
        />
        <WaterfallRow
          label={<MetricLabel metric="after_tax_cf">After-Tax Cash Flow</MetricLabel>}
          value={fmtDollar(afterTaxCashFlow)}
          type="subtotal"
          bold
        />
      </div>

      {/* Divider */}
      <div className="border-t border-gold/30 my-3" />

      {/* Section 3: Cash Flow → Return */}
      <div>
        <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">Cash Flow to Return</div>
        <WaterfallRow
          label={<MetricLabel metric="equity_invested">Equity Invested</MetricLabel>}
          value={fmtDollar(equityInvested)}
        />
        <WaterfallRow
          label={<MetricLabel metric="coc">Cash-on-Cash Return</MetricLabel>}
          value={fmtPct(cashOnCash)}
          type="result"
          bold
          large
        />
      </div>
    </div>
  );
}
